import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Eye, Users, RefreshCw, Link2, CreditCard, Check, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { DataTable, Column } from '@/components/ui/DataTable';
import { usePlans, useStripeProducts, useSyncStripeProduct, useCreateStripeProduct } from '@/hooks/usePlans';
import { useStripeStatus } from '@/hooks/useSettings';
import type { PlanWithLimits, StripeProduct, PlanSyncMapping, CreateStripeProductDto } from '@/types/admin.types';

const formatCurrency = (amount: string | number, currency: string) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-GB', {
    style: 'currency',
    currency,
  }).format(num);
};

const formatStripePrice = (amount: number, currency: string) => {
  return new Intl.NumberFormat(currency === 'brl' ? 'pt-BR' : 'en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

const defaultNewProduct: CreateStripeProductDto = {
  slug: '',
  name: '',
  planType: 'basic',
  currency: 'BRL',
  priceMonthly: 0,
  priceYearly: 0,
  description: '',
  features: [],
  displayOrder: 1,
};

export const PlansListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: plans, isLoading } = usePlans();
  const { data: stripeProducts, isLoading: loadingProducts, refetch: refetchProducts } = useStripeProducts();
  const { data: stripeStatus } = useStripeStatus();
  const syncProductMutation = useSyncStripeProduct();
  const createStripeProductMutation = useCreateStripeProduct();

  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(null);
  const [syncMapping, setSyncMapping] = useState<PlanSyncMapping>({
    productId: '',
    slug: '',
    planType: 'basic',
    displayOrder: 1,
  });
  const [newProduct, setNewProduct] = useState<CreateStripeProductDto>(defaultNewProduct);
  const [featuresText, setFeaturesText] = useState('');

  const handleOpenSyncModal = (product: StripeProduct) => {
    setSelectedProduct(product);
    setSyncMapping({
      productId: product.id,
      slug: product.name.toLowerCase().replace(/\s+/g, '-'),
      planType: 'basic',
      displayOrder: 1,
    });
    setShowSyncModal(true);
  };

  const handleSyncProduct = async () => {
    if (!selectedProduct) return;
    await syncProductMutation.mutateAsync(syncMapping);
    setShowSyncModal(false);
    setSelectedProduct(null);
  };

  const handleCreateStripeProduct = async () => {
    const features = featuresText.split('\n').map(f => f.trim()).filter(f => f.length > 0);
    await createStripeProductMutation.mutateAsync({
      ...newProduct,
      features,
    });
    setShowCreateModal(false);
    setNewProduct(defaultNewProduct);
    setFeaturesText('');
  };

  const columns: Column<PlanWithLimits>[] = [
    {
      key: 'name',
      label: 'Nome',
      render: (plan) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{plan.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{plan.slug}</p>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (plan) => (
        <Badge variant={plan.type === 'pro' ? 'primary' : plan.type === 'enterprise' ? 'info' : 'default'}>
          {plan.type.charAt(0).toUpperCase() + plan.type.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'priceMonthly',
      label: 'Preco/mes',
      render: (plan) => (
        <span className="font-semibold">{formatCurrency(plan.priceMonthly, plan.currency)}</span>
      ),
    },
    {
      key: 'usersCount',
      label: 'Usuarios',
      align: 'center',
      render: (plan) => (
        <div className="flex items-center justify-center gap-1">
          <Users className="w-4 h-4 text-gray-400" />
          <span>{plan.usersCount || 0}</span>
        </div>
      ),
    },
    {
      key: 'isActive',
      label: 'Status',
      align: 'center',
      render: (plan) => (
        <Badge variant={plan.isActive ? 'success' : 'default'} dot>
          {plan.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Acoes',
      align: 'right',
      render: (plan) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/plans/${plan.id}`);
            }}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/plans/${plan.id}`);
            }}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Planos</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Gerencie os planos de assinatura
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/admin/plans/new')}>
          Criar Plano
        </Button>
      </div>

      {/* Stripe Products Card */}
      {stripeStatus?.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Produtos do Stripe
                {stripeStatus?.testMode && (
                  <Badge variant="warning" size="sm">Modo Teste</Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowCreateModal(true)}
                >
                  Criar Produto
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RefreshCw className={`w-4 h-4 ${loadingProducts ? 'animate-spin' : ''}`} />}
                  onClick={() => refetchProducts()}
                  disabled={loadingProducts}
                >
                  Atualizar
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingProducts ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : stripeProducts && stripeProducts.length > 0 ? (
              <div className="space-y-4">
                {stripeProducts.map((product) => {
                  const prices = Array.isArray(product.prices) ? product.prices : [];
                  const monthlyPrice = prices.find(p => p.interval === 'month' && p.active);
                  const yearlyPrice = prices.find(p => p.interval === 'year' && p.active);
                  const isLinked = plans?.some(p =>
                    (p.stripePriceIdMonthly && prices.some(pr => pr.id === p.stripePriceIdMonthly)) ||
                    (p.stripePriceIdYearly && prices.some(pr => pr.id === p.stripePriceIdYearly))
                  );

                  return (
                    <div
                      key={product.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                          {isLinked && (
                            <Badge variant="success" size="sm">
                              <Check className="w-3 h-3 mr-1" />
                              Vinculado
                            </Badge>
                          )}
                        </div>
                        {product.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{product.description}</p>
                        )}
                        <div className="flex gap-4 text-sm">
                          {monthlyPrice && (
                            <span>
                              Mensal: <strong>{formatStripePrice(monthlyPrice.unitAmount, monthlyPrice.currency)}</strong>
                            </span>
                          )}
                          {yearlyPrice && (
                            <span>
                              Anual: <strong>{formatStripePrice(yearlyPrice.unitAmount, yearlyPrice.currency)}</strong>
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">ID: {product.id}</p>
                      </div>
                      <div className="mt-4 sm:mt-0">
                        {!isLinked ? (
                          <Button
                            size="sm"
                            leftIcon={<Link2 className="w-4 h-4" />}
                            onClick={() => handleOpenSyncModal(product)}
                          >
                            Vincular
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<RefreshCw className="w-4 h-4" />}
                            onClick={() => handleOpenSyncModal(product)}
                          >
                            Atualizar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <AlertCircle className="w-8 h-8 mb-2" />
                <p>Nenhum produto encontrado no Stripe</p>
                <p className="text-sm">Crie produtos no dashboard do Stripe para sincronizar</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plans Table */}
      <Card>
        <DataTable
          data={plans || []}
          columns={columns}
          keyExtractor={(plan) => plan.id}
          isLoading={isLoading}
          emptyMessage="Nenhum plano encontrado"
          onRowClick={(plan) => navigate(`/admin/plans/${plan.id}`)}
        />
      </Card>

      {/* Sync Modal */}
      <Modal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        title="Vincular Produto do Stripe"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowSyncModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSyncProduct}
              isLoading={syncProductMutation.isPending}
              disabled={!syncMapping.slug}
            >
              Vincular
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {selectedProduct && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <p className="font-medium">{selectedProduct.name}</p>
              <p className="text-sm text-gray-500">{selectedProduct.id}</p>
            </div>
          )}

          <Input
            label="Slug do Plano"
            value={syncMapping.slug}
            onChange={(e) => setSyncMapping({ ...syncMapping, slug: e.target.value })}
            placeholder="ex: basic-br, pro-gb"
          />

          <Select
            label="Tipo do Plano"
            value={syncMapping.planType}
            onChange={(e) => setSyncMapping({ ...syncMapping, planType: e.target.value as 'basic' | 'pro' | 'enterprise' })}
            options={[
              { value: 'basic', label: 'Basic' },
              { value: 'pro', label: 'Pro' },
              { value: 'enterprise', label: 'Enterprise' },
            ]}
          />

          <Input
            type="number"
            label="Ordem de Exibicao"
            value={syncMapping.displayOrder}
            onChange={(e) => setSyncMapping({ ...syncMapping, displayOrder: parseInt(e.target.value) || 1 })}
            min={1}
          />

          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>Ao vincular, os dados do produto (nome, preco, moeda) serao importados do Stripe.</p>
          </div>
        </div>
      </Modal>

      {/* Create Stripe Product Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Criar Produto no Stripe"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateStripeProduct}
              isLoading={createStripeProductMutation.isPending}
              disabled={!newProduct.name || !newProduct.slug || !newProduct.priceMonthly}
            >
              Criar Produto
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome do Produto"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
              placeholder="ex: KeepNota Basic"
            />
            <Input
              label="Slug"
              value={newProduct.slug}
              onChange={(e) => setNewProduct({ ...newProduct, slug: e.target.value })}
              placeholder="ex: basic-br"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Tipo do Plano"
              value={newProduct.planType}
              onChange={(e) => setNewProduct({ ...newProduct, planType: e.target.value as 'basic' | 'pro' | 'enterprise' })}
              options={[
                { value: 'basic', label: 'Basic' },
                { value: 'pro', label: 'Pro' },
                { value: 'enterprise', label: 'Enterprise' },
              ]}
            />
            <Select
              label="Moeda"
              value={newProduct.currency}
              onChange={(e) => setNewProduct({ ...newProduct, currency: e.target.value as 'BRL' | 'GBP' | 'USD' })}
              options={[
                { value: 'BRL', label: 'BRL (R$)' },
                { value: 'GBP', label: 'GBP (£)' },
                { value: 'USD', label: 'USD ($)' },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              step="0.01"
              label="Preco Mensal"
              value={newProduct.priceMonthly || ''}
              onChange={(e) => setNewProduct({ ...newProduct, priceMonthly: parseFloat(e.target.value) || 0 })}
              placeholder="19.90"
            />
            <Input
              type="number"
              step="0.01"
              label="Preco Anual (opcional)"
              value={newProduct.priceYearly || ''}
              onChange={(e) => setNewProduct({ ...newProduct, priceYearly: parseFloat(e.target.value) || undefined })}
              placeholder="199.00"
            />
          </div>

          <Input
            type="number"
            label="Ordem de Exibicao"
            value={newProduct.displayOrder || 1}
            onChange={(e) => setNewProduct({ ...newProduct, displayOrder: parseInt(e.target.value) || 1 })}
            min={1}
          />

          <Textarea
            label="Descricao"
            value={newProduct.description || ''}
            onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
            placeholder="Descricao do plano..."
            rows={2}
          />

          <Textarea
            label="Features (uma por linha)"
            value={featuresText}
            onChange={(e) => setFeaturesText(e.target.value)}
            placeholder="100 fotos/mes&#10;50 audios/mes&#10;Relatorio anual"
            rows={4}
          />

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Isso vai criar um produto no Stripe com os precos configurados e tambem salvar o plano localmente.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlansListPage;
